import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  TextField,
  Select,
  TableMetadata,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { RequestCountyFilterSelect } from "./components";
import { REQUEST_COUNTY_FILTER_ACCESS } from "./constants";
import {
  createInitialInstitutionIds,
  getInitialTableState,
  sanitizeRequestSearchApiPayload,
} from "./utils";
import {
  searchRequestMetadata,
  getRequestExport,
} from "../../api/Request/RequestApi";
import {
  ContentArea,
  InstitutionSelect,
  ListTable,
  SearchCard,
  ToolbarTop,
} from "../../components";
import {
  useInput,
  useFilterPersistence,
  useAuth,
  useEnumCodeBooks,
  useTableStatePersistence,
} from "../../hooks";
import { Role } from "../../models";
import {
  RequestMetadata,
  RequestMetadataSearch,
  RequestSort,
  RequestStatus,
} from "../../models/Request";
import { ClaimType } from "../../models/User";
import {
  useGetSchoolYearListQuery,
  useGetSchoolYearCurrentQuery,
} from "../../queries/codeBookQuery";
import {
  requestCreateRoute,
  requestEditUrl,
  requestDetailsUrl,
} from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";

const STORAGE_KEY = "RequestSearchTableState";

export function RequestList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const auth = useAuth();
  const { requestTypeCodeBook, requestStatusCodeBook } = useEnumCodeBooks();
  const { data: schoolYear } = useGetSchoolYearCurrentQuery();
  const { data: schoolYears } = useGetSchoolYearListQuery();

  const INITIAL_TABLE_STATE = useMemo(
    () => getInitialTableState(STORAGE_KEY),
    []
  );

  const INITIAL_INSTITUTION_IDS = useMemo(
    () => createInitialInstitutionIds(auth.currentRole),
    [auth.currentRole]
  );

  const INITIAL_REQUEST_METADATA_SEARCH = useMemo(
    () => ({
      schoolYearId: schoolYear?.id,
      countyId: INITIAL_TABLE_STATE.countyId,
      institutionIds: INITIAL_TABLE_STATE.institutionIds
        ? INITIAL_TABLE_STATE.institutionIds
        : INITIAL_INSTITUTION_IDS,
      code: INITIAL_TABLE_STATE.code,
      firstName: INITIAL_TABLE_STATE.firstName,
      lastName: INITIAL_TABLE_STATE.lastName,
      oib: INITIAL_TABLE_STATE.oib,
      requestType: INITIAL_TABLE_STATE.requestType,
      requestStatus: INITIAL_TABLE_STATE.requestStatus,
    }),
    [schoolYear?.id, INITIAL_TABLE_STATE, INITIAL_INSTITUTION_IDS]
  );

  //TABLE DATA
  const defaultTableData = useMemo(
    () => TableUtils.tableDefaultData<RequestMetadata>(),
    []
  );
  const [tableData, setTableData] =
    useState<PagedResult<RequestMetadata>>(defaultTableData);

  //SET SEARCH FILTERS
  const {
    filterState: requestMetadataSearch,
    setFilterState: setRequestMetadataSearch,
    saveFilters,
    clearFilters,
    filterChanged,
    setClearFilter,
  } = useFilterPersistence<RequestMetadataSearch>({
    storageKey: STORAGE_KEY,
    initialState: INITIAL_REQUEST_METADATA_SEARCH,
  });

  //TABLE METADATA
  const tableMetadata: TableMetadata<RequestMetadata> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "code",
        header: t("common.schoolYearModel.code"),
        accessor: (item: RequestMetadata) => {
          return <div className={"text-nowrap text-right"}>{item.code}</div>;
        },
        sortBy: RequestSort.Code,
      },
      {
        columnKey: "firstName",
        header:
          t("common.schoolCandidateModel.lastName") +
          "/" +
          t("common.schoolCandidateModel.firstName"),
        accessor: (item: RequestMetadata) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.lastName},&nbsp;{item.firstName}
              </div>
              <div className={"text-nowrap"}>
                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.childOib}
              </div>
            </div>
          );
        },
        sortBy: RequestSort.LastName,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item: RequestMetadata) => {
          return (
            <div className={"text-nowrap text-right"}>
              {item.dateOfBirth ? hrDate(item.dateOfBirth) : ""}
            </div>
          );
        },
        sortBy: RequestSort.Birthday,
      },
      {
        columnKey: "permanentAddress",
        header:
          t("common.schoolCandidateModel.permanentAddress") +
          "/" +
          t("common.schoolCandidateModel.temporaryAddress"),
        accessor: (item: RequestMetadata) => {
          return (
            <>
              <div className={"text-nowrap"}>
                <b>
                  {item.permanentAddress?.street}&nbsp;
                  {item.permanentAddress?.houseNumber},&nbsp;
                  {item.permanentAddress?.town}{" "}
                </b>
              </div>
              {item.temporaryAddress && (
                <div className={"text-nowrap"}>
                  {item.temporaryAddress.street}&nbsp;
                  {item.temporaryAddress.houseNumber},&nbsp;
                  {item.temporaryAddress.town}{" "}
                </div>
              )}
            </>
          );
        },
        sortBy: RequestSort.Address,
      },
      {
        columnKey: "type",
        header: t("common.requestModel.requestType"),
        accessor: (item: RequestMetadata) => {
          return (
            <Label
              color={FormatUtils.getRequestTypeColor(item.requestType)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {requestTypeCodeBook.find((v) => v.id === item.requestType)?.name}
            </Label>
          );
        },
        sortBy: RequestSort.Type,
      },
      {
        columnKey: "status",
        header: t("common.requestModel.requestStatus"),
        accessor: (item: RequestMetadata) => {
          return (
            <Label
              color={FormatUtils.getRequestStatusColor(item.status)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {requestStatusCodeBook.find((v) => v.id === item.status)?.name}
            </Label>
          );
        },
        sortBy: RequestSort.Status,
      },
      {
        columnKey: "isAdminCreated",
        header: t("common.requestModel.isAdminCreated"),
        accessor: (item: RequestMetadata) => {
          return (
            <>
              {item.isAdminCreated
                ? t("common.requestModel.adminCreated")
                : t("common.requestModel.parentCreated")}
            </>
          );
        },
      },
      {
        columnKey: "isHandicapped",
        header: t("common.schoolCandidateModel.isHandicapped"),
        accessor: (item: RequestMetadata) => {
          return (
            <>
              <div className={"text-nowrap"}>
                {item.isHandicapped ? t("common.yes") : t("common.no")}
              </div>
            </>
          );
        },
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: RequestMetadata) => {
          const isDraft = item.status === RequestStatus.Draft;
          const urlFunction = isDraft ? requestEditUrl : requestDetailsUrl;
          const ariaLabel = isDraft ? t("common.edit") : t("common.details");
          
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={ariaLabel}
                onClick={() => handleDetailsClicked(item.id, urlFunction)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<RequestMetadataSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: INITIAL_TABLE_STATE.page,
    paginationPageSize: INITIAL_TABLE_STATE.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: INITIAL_TABLE_STATE.order,
      sortBy: INITIAL_TABLE_STATE.sortBy,
    },
  });

  const canApplyCountyFilter = useMemo(
    () => auth.hasRoleAccess(REQUEST_COUNTY_FILTER_ACCESS),
    [auth]
  );

  useEffect(() => {
    if (!requestMetadataSearch.schoolYearId && schoolYear && schoolYear.id) {
      setRequestMetadataSearch({
        ...requestMetadataSearch,
        schoolYearId: schoolYear.id,
      });
    }
  }, [schoolYear, requestMetadataSearch.schoolYearId]);

  useEffect(() => {
    (async () => {
      const request = TableUtils.GetSearchRequest<RequestMetadataSearch>(
        requestMetadataSearch,
        tableState
      );
      const itemList = await searchRequestMetadata(
        sanitizeRequestSearchApiPayload(request, canApplyCountyFilter)
      );
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
  }, [tableState, canApplyCountyFilter]);

  const handleInstitutionChange = (id: number) => {
    setRequestMetadataSearch({
      ...requestMetadataSearch,
      institutionIds: !isNaN(id) ? [id] : [],
    });
  };

  const { handleDetailsClicked } = useTableStatePersistence<RequestMetadataSearch>({
    storageKey: STORAGE_KEY,
    requestDataSearch: requestMetadataSearch,
    tableState,
  });
  
  const handleNewClicked = () => {
    history.push(requestCreateRoute);
  };

  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getRequestExport(
      sanitizeRequestSearchApiPayload(
        requestMetadataSearch,
        canApplyCountyFilter
      ),
      exportType
    );
  };

  const doSearch = () => {
    setFilters({ ...requestMetadataSearch });
  };

  const handleClearSearch = () => {
    setClearFilter(true);
    const defaultSearch = {
      schoolYearId: schoolYear?.id,
      institutionIds: INITIAL_INSTITUTION_IDS,
      countyId: undefined,
    };
    setRequestMetadataSearch(defaultSearch);
    clearFilters();
    doSearch();
  };

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("common.requestModel.pageName")}
        showAdd={
          auth.hasRoleAccess({
            whitelist: [
              Role.InstitutionPrincipal,
              Role.InstitutionAdministrator,
              Role.CountyAdministrator,
              Role.CountyLeader,
            ],
          })
            ? "show"
            : "hide"
        }
        onClickAdd={handleNewClicked}
      />
      <SearchCard
        onClickSearch={() => {
          doSearch();
        }}
        onClickClearSearch={handleClearSearch}
        isFilterChanged={filterChanged}
        cardTitle={t("common.requestModel.searchCardName")}
        hideDivider
      >
        <Row>
          <Col md={12}>
            <Select
              label={t("common.schoolCandidateModel.schoolYearId")}
              name={nameof<typeof requestMetadataSearch>("schoolYearId")}
              value={requestMetadataSearch.schoolYearId}
              required={true}
              onChange={handleChange(setRequestMetadataSearch)}
              selectData={codeBookToSelectData(schoolYears)}
            />
          </Col>
        </Row>
        {canApplyCountyFilter && (
          <Row>
            <Col md={6}>
              <RequestCountyFilterSelect
                value={requestMetadataSearch.countyId}
                setFilterState={setRequestMetadataSearch}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={6}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof requestMetadataSearch>("institutionIds")}
              value={
                requestMetadataSearch.institutionIds &&
                requestMetadataSearch.institutionIds.length > 0
                  ? requestMetadataSearch.institutionIds[0]
                  : undefined
              }
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onlyByEnrollmentInstitution
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              showUser
              shouldCheckParentCounty
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolYearModel.code")}
              name={nameof<typeof requestMetadataSearch>("code")}
              value={requestMetadataSearch.code}
              onChange={handleChange(setRequestMetadataSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              name={nameof<typeof requestMetadataSearch>("lastName")}
              value={requestMetadataSearch.lastName}
              onChange={handleChange(setRequestMetadataSearch)}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              name={nameof<typeof requestMetadataSearch>("firstName")}
              value={requestMetadataSearch.firstName}
              onChange={handleChange(setRequestMetadataSearch)}
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              name={nameof<typeof requestMetadataSearch>("oib")}
              value={requestMetadataSearch.oib}
              onChange={handleChange(setRequestMetadataSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Select
              label={t("common.requestModel.requestType")}
              name={nameof<typeof requestMetadataSearch>("requestType")}
              value={requestMetadataSearch.requestType}
              onChange={handleChange(setRequestMetadataSearch)}
              selectData={codeBookToSelectData(requestTypeCodeBook)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
          <Col md={6}>
            <Select
              label={t("common.requestModel.requestStatus")}
              name={nameof<typeof requestMetadataSearch>("requestStatus")}
              value={requestMetadataSearch.requestStatus}
              onChange={handleChange(setRequestMetadataSearch)}
              selectData={codeBookToSelectData(requestStatusCodeBook)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload
      />
    </ContentArea>
  );
}
